import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { caseService, inputService, judicialService, api } from "@/services/api";
import { mapDbCaseToFrontend, mapDbInterventionToFrontend } from "@/services/mapping";

import type { UserProfile } from "@/components/feature/Sidebar";
import { SkeletonHero, SkeletonKPI, SkeletonCard } from "@/components/base/Skeleton";
import CriticalActionBanner from "@/components/feature/CriticalActionBanner";
import NextBestAction from "@/components/feature/NextBestAction";
import WorkflowPath, { type WorkflowStage } from "@/components/feature/WorkflowPath";
import MissionCard from "@/components/feature/MissionCard";
import CompletionRing from "@/components/feature/CompletionRing";
import ScrollReveal, { ScrollRevealContainer } from "@/components/feature/ScrollReveal";

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

function daysBetween(d1: string, d2: string): number {
  return Math.ceil((new Date(d2).getTime() - new Date(d1).getTime()) / (1000 * 60 * 60 * 24));
}

// ── Role color config ──
const ROLE_COLORS: Record<string, { gradient: string; accent: string; light: string; text: string }> = {
  director: { gradient: "from-foreground-800 to-foreground-900", accent: "bg-foreground-500", light: "bg-foreground-50", text: "text-foreground-700" },
  convivencia: { gradient: "from-primary-600 via-primary-700 to-primary-800", accent: "bg-primary-500", light: "bg-primary-50", text: "text-primary-700" },
  orientador: { gradient: "from-purple-600 via-purple-700 to-amber-700", accent: "bg-purple-500", light: "bg-purple-50", text: "text-purple-700" },
  psicologo: { gradient: "from-teal-600 via-teal-700 to-emerald-700", accent: "bg-teal-500", light: "bg-teal-50", text: "text-teal-700" },
  inspector: { gradient: "from-primary-600 via-primary-700 to-red-700", accent: "bg-primary-500", light: "bg-primary-50", text: "text-primary-700" },
  profesor_jefe: { gradient: "from-primary-600 via-primary-700 to-sky-700", accent: "bg-primary-500", light: "bg-primary-50", text: "text-primary-700" },
  docente: { gradient: "from-emerald-500 via-emerald-600 to-teal-600", accent: "bg-emerald-500", light: "bg-emerald-50", text: "text-emerald-700" },
  admin: { gradient: "from-foreground-700 via-foreground-800 to-foreground-900", accent: "bg-foreground-500", light: "bg-foreground-50", text: "text-foreground-700" },
  sostenedor: { gradient: "from-foreground-800 via-primary-800 to-foreground-900", accent: "bg-foreground-500", light: "bg-foreground-50", text: "text-foreground-700" },
};

// ── Role-specific sub-actions ──
const ROLE_QUICK_ACCESS: Record<string, Array<{ label: string; icon: string; path: string; color: string; bg: string }>> = {
  director: [
    { label: "Convivencia", icon: "ri-shield-user-line", path: "/convivencia", color: "text-primary-500", bg: "bg-primary-50" },
    { label: "Orientación", icon: "ri-compass-3-line", path: "/orientacion", color: "text-purple-500", bg: "bg-purple-50" },
    { label: "Psicología", icon: "ri-psychotherapy-line", path: "/psicologo", color: "text-teal-500", bg: "bg-teal-50" },
    { label: "Judicial", icon: "ri-scales-3-line", path: "/judicial", color: "text-amber-500", bg: "bg-amber-50" },
    { label: "Reportes", icon: "ri-file-chart-line", path: "/director/reportes/consolidado", color: "text-foreground-500", bg: "bg-foreground-50" },
    { label: "IA", icon: "ri-robot-2-line", path: "/ia/consulta", color: "text-sky-500", bg: "bg-sky-50" },
  ],
  convivencia: [
    { label: "Nuevo caso", icon: "ri-add-circle-line", path: "/convivencia/casos/nuevo", color: "text-primary-500", bg: "bg-primary-50" },
    { label: "Casos activos", icon: "ri-folder-3-line", path: "/convivencia/casos", color: "text-primary-500", bg: "bg-primary-50" },
    { label: "Entrevistas", icon: "ri-mic-line", path: "/convivencia/entrevistas", color: "text-emerald-500", bg: "bg-emerald-50" },
    { label: "Medidas", icon: "ri-hand-heart-line", path: "/convivencia/medidas", color: "text-amber-500", bg: "bg-amber-50" },
    { label: "Derivaciones", icon: "ri-share-forward-line", path: "/convivencia/derivaciones", color: "text-purple-500", bg: "bg-purple-50" },
    { label: "IA", icon: "ri-robot-2-line", path: "/ia/consulta", color: "text-sky-500", bg: "bg-sky-50" },
  ],
  orientador: [
    { label: "Casos", icon: "ri-folder-3-line", path: "/orientacion/casos", color: "text-purple-500", bg: "bg-purple-50" },
    { label: "Judicializados", icon: "ri-scales-3-line", path: "/orientacion/judicializados", color: "text-red-500", bg: "bg-red-50" },
    { label: "Informes", icon: "ri-file-warning-line", path: "/orientacion/informes", color: "text-amber-500", bg: "bg-amber-50" },
    { label: "Insumos", icon: "ri-send-plane-line", path: "/orientacion/insumos", color: "text-amber-500", bg: "bg-amber-50" },
    { label: "Judicial", icon: "ri-scales-3-line", path: "/judicial", color: "text-purple-500", bg: "bg-purple-50" },
    { label: "IA", icon: "ri-robot-2-line", path: "/ia/consulta", color: "text-sky-500", bg: "bg-sky-50" },
  ],
  psicologo: [
    { label: "Casos", icon: "ri-folder-3-line", path: "/psicologo/casos", color: "text-teal-500", bg: "bg-teal-50" },
    { label: "Intervenciones", icon: "ri-heart-pulse-line", path: "/psicologo/intervenciones", color: "text-teal-500", bg: "bg-teal-50" },
    { label: "Seguimiento", icon: "ri-user-heart-line", path: "/psicologo/seguimiento", color: "text-emerald-500", bg: "bg-emerald-50" },
    { label: "Informes", icon: "ri-file-text-line", path: "/psicologo/informes", color: "text-amber-500", bg: "bg-amber-50" },
    { label: "Derivaciones", icon: "ri-share-forward-line", path: "/psicologo/derivaciones-externas", color: "text-red-500", bg: "bg-red-50" },
    { label: "IA", icon: "ri-robot-2-line", path: "/ia/consulta", color: "text-sky-500", bg: "bg-sky-50" },
  ],
  inspector: [
    { label: "Incidentes", icon: "ri-add-circle-line", path: "/convivencia/casos/nuevo", color: "text-primary-500", bg: "bg-primary-50" },
    { label: "Casos", icon: "ri-folder-3-line", path: "/convivencia/casos", color: "text-primary-500", bg: "bg-primary-50" },
    { label: "Protocolos", icon: "ri-book-open-line", path: "/ia/protocolos", color: "text-emerald-500", bg: "bg-emerald-50" },
    { label: "Derivar", icon: "ri-share-forward-line", path: "/convivencia/derivaciones", color: "text-amber-500", bg: "bg-amber-50" },
  ],
  profesor_jefe: [
    { label: "Solicitudes", icon: "ri-inbox-line", path: "/profesor-jefe/solicitudes", color: "text-amber-500", bg: "bg-amber-50" },
    { label: "Estudiantes", icon: "ri-team-line", path: "/estudiantes", color: "text-primary-500", bg: "bg-primary-50" },
    { label: "Formularios", icon: "ri-file-text-line", path: "/profesor-jefe/formularios", color: "text-primary-500", bg: "bg-primary-50" },
    { label: "IA", icon: "ri-robot-2-line", path: "/ia/consulta", color: "text-sky-500", bg: "bg-sky-50" },
  ],
  docente: [
    { label: "Mis Solicitudes", icon: "ri-inbox-line", path: "/docente", color: "text-emerald-500", bg: "bg-emerald-50" },
    { label: "Completar", icon: "ri-file-text-line", path: "/docente/solicitudes", color: "text-amber-500", bg: "bg-amber-50" },
    { label: "Historial", icon: "ri-history-line", path: "/docente/historial", color: "text-primary-500", bg: "bg-primary-50" },
    { label: "IA", icon: "ri-robot-2-line", path: "/ia/consulta", color: "text-sky-500", bg: "bg-sky-50" },
  ],
};

const DEFAULT_QUICK_ACCESS = [
  { label: "Casos", icon: "ri-folder-3-line", path: "/convivencia/casos", color: "text-primary-500", bg: "bg-primary-50" },
  { label: "Protocolos", icon: "ri-book-open-line", path: "/ia/protocolos", color: "text-emerald-500", bg: "bg-emerald-50" },
  { label: "IA", icon: "ri-robot-2-line", path: "/ia/consulta", color: "text-sky-500", bg: "bg-sky-50" },
  { label: "Estudiantes", icon: "ri-team-line", path: "/estudiantes", color: "text-purple-500", bg: "bg-purple-50" },
];

// ── Mission badge logic ──
function getMissionStatus(caso: any, todayStr: string, casoInterventions: Record<string, any[]>, insumosVencidos: any[]): { status: "urgent" | "active" | "pending" | "blocked"; badge: string; color: "red" | "amber" | "emerald" | "primary" } {
  const ints = casoInterventions[caso.id] || [];
  const lastInt = ints.length > 0 ? ints.reduce((latest, curr) => curr.fecha > latest.fecha ? curr : latest, ints[0]) : null;
  const daysSince = lastInt ? daysBetween(lastInt.fecha, todayStr) : daysBetween(caso.fechaApertura, todayStr);
  const hasOverdueInput = insumosVencidos.some((t) => t.caseId === caso.id);

  if (caso.prioridad === "critica" && (caso.estado === "abierto" || hasOverdueInput)) return { status: "urgent", badge: "URGENTE", color: "red" };
  if (daysSince > 7) return { status: "urgent", badge: "DETENIDO", color: "red" };
  if (caso.estado === "en_intervencion" || caso.estado === "en_seguimiento") return { status: "active", badge: "EN CURSO", color: "primary" };
  if (caso.estado === "esperando_insumos" || caso.estado === "esperando_informe_docente" || caso.estado === "esperando_validacion") return { status: "pending", badge: "ESPERANDO", color: "amber" };
  return { status: "pending", badge: "PENDIENTE", color: "amber" };
}

export default function ResumenPage() {
  const [user, setUser] = useState<UserProfile | null>(getStoredUser);
  const [greeting, setGreeting] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [cases, setCases] = useState<any[]>([]);
  const [courtRequestsState, setCourtRequestsState] = useState<any[]>([]);
  const [teacherInputsState, setTeacherInputsState] = useState<any[]>([]);
  const [interventionsState, setInterventionsState] = useState<any[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Buenos días");
    else if (hour < 19) setGreeting("Buenas tardes");
    else setGreeting("Buenas noches");
  }, []);

  useEffect(() => {
    async function loadDashboardData() {
      try {
        setLoading(true);
        setError("");
        const [casesData, inputsData, judicialData, interventionsData] = await Promise.all([
          caseService.list(),
          inputService.listPendientes(),
          judicialService.list(),
          api.get("/intervenciones").then(res => res.data)
        ]);

        if (Array.isArray(casesData)) {
          setCases(casesData.map(mapDbCaseToFrontend));
        } else {
          setCases([]);
        }

        if (Array.isArray(inputsData)) {
          setTeacherInputsState(inputsData.map(dbInput => ({
            id: dbInput.id,
            caseId: dbInput.caso_id,
            docenteName: dbInput.docente_nombre || "Docente",
            studentName: dbInput.estudiante_nombre || "Estudiante",
            materia: "Asignatura",
            fechaLimite: dbInput.fecha_limite ? new Date(dbInput.fecha_limite).toISOString().slice(0, 10) : "",
            estado: dbInput.estado || "pendiente"
          })));
        } else {
          setTeacherInputsState([]);
        }

        if (Array.isArray(judicialData)) {
          setCourtRequestsState(judicialData.map(dbJud => ({
            id: dbJud.id,
            caseId: dbJud.caso_id,
            tribunal: dbJud.tribunal,
            rit: dbJud.rit_ruc,
            fechaVencimiento: dbJud.fecha_vencimiento ? new Date(dbJud.fecha_vencimiento).toISOString().slice(0, 10) : "",
            fechaLimite: dbJud.fecha_vencimiento ? new Date(dbJud.fecha_vencimiento).toISOString().slice(0, 10) : "",
            estado: dbJud.estado || "pendiente",
            tipoInforme: dbJud.tipo_requerimiento || "informe_social"
          })));
        } else {
          setCourtRequestsState([]);
        }

        if (Array.isArray(interventionsData)) {
          setInterventionsState(interventionsData.map(mapDbInterventionToFrontend));
        } else {
          setInterventionsState([]);
        }
      } catch (err: any) {
        console.error("Failed to load dashboard data from API:", err);
        setError("Error al cargar resumen desde base de datos Azure: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    if (user) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const todayStr = "2026-06-10";

  // All computed data - memoized
  const data = useMemo(() => {
    if (!user) return null;
    const role = user.rol;

    const casosActivos = cases.filter((c) => c.estado !== "cerrado");
    const casosCriticos = casosActivos.filter((c) => c.prioridad === "critica");
    const casosCerradosMes = cases.filter((c) => c.estado === "cerrado" && c.fechaCierre && c.fechaCierre >= "2026-05-01");

    const activeCourtRequests = courtRequestsState.filter((cr) => cr.estado !== "enviado");
    const informesVencidos = activeCourtRequests.filter((cr) => daysBetween(todayStr, cr.fechaLimite) < 0);

    const insumosVencidos = teacherInputsState.filter((t) => t.estado === "vencido" || (t.estado === "pendiente" && daysBetween(todayStr, t.fechaLimite) < 0));
    const insumosPendientes = teacherInputsState.filter((t) => t.estado === "pendiente" || t.estado === "solicitado" || t.estado === "en_desarrollo");

    const intervencionesSemana = interventionsState.filter((i) => {
      const d = daysBetween(i.fecha, todayStr);
      return d >= 0 && d <= 7;
    });

    const casoInterventions: Record<string, any[]> = {};
    interventionsState.forEach((i) => {
      if (!casoInterventions[i.caseId]) casoInterventions[i.caseId] = [];
      casoInterventions[i.caseId].push(i);
    });

    const casosEstancados = casosActivos.filter((c) => {
      const ints = casoInterventions[c.id] || [];
      if (ints.length === 0) return daysBetween(c.fechaApertura, todayStr) > 7;
      const lastInt = ints.reduce((latest, curr) => curr.fecha > latest.fecha ? curr : latest, ints[0]);
      return daysBetween(lastInt.fecha, todayStr) > 7;
    });

    // Role-specific case filtering
    let roleCases = casosActivos;
    if (role === "docente") {
      const docenteRequests = teacherInputsState.filter((t) => t.docenteName === user.nombre);
      const caseIds = docenteRequests.map((t) => t.caseId).filter(Boolean);
      roleCases = casosActivos.filter((c) => caseIds.includes(c.id));
    } else if (role === "profesor_jefe") {
      const profeRequests = teacherInputsState.filter((t) => t.docenteName === user.nombre);
      const caseIds = profeRequests.map((t) => t.caseId).filter(Boolean);
      roleCases = casosActivos.filter((c) => caseIds.includes(c.id));
    } else if (role === "inspector") {
      roleCases = casosActivos.filter((c) => ["convivencia", "conducta"].includes(c.categoria));
    } else if (role === "convivencia") {
      roleCases = casosActivos.filter((c) => ["convivencia", "conducta", "asistencia", "apoderado"].includes(c.categoria));
    } else if (role === "orientador") {
      roleCases = casosActivos.filter((c) => ["orientacion", "judicializado", "asistencia"].includes(c.categoria));
    } else if (role === "psicologo") {
      roleCases = casosActivos.filter((c) => ["psicologia", "vulneracion", "orientacion"].includes(c.categoria));
    }
    // director and sostenedor see all
    const roleCriticos = roleCases.filter((c) => c.prioridad === "critica");

    // Missions: top 4 most critical cases
    const missions = roleCases
      .filter((c) => c.estado !== "cerrado")
      .sort((a, b) => {
        const priOrder: Record<string, number> = { critica: 0, alta: 1, media: 2, baja: 3 };
        return (priOrder[a.prioridad] ?? 2) - (priOrder[b.prioridad] ?? 2);
      })
      .slice(0, 4);

    // Critical actions for banner
    const criticalActions = [];
    const overdueDocs = insumosVencidos.filter((t) => roleCases.some((c) => c.id === t.caseId));
    if (overdueDocs.length > 0 && (role === "orientador" || role === "director" || role === "sostenedor" || role === "convivencia")) {
      criticalActions.push({ label: `${overdueDocs.length} docente${overdueDocs.length !== 1 ? "s" : ""} no ha${overdueDocs.length !== 1 ? "n" : ""} enviado insumos`, icon: "ri-send-plane-line", count: overdueDocs.length, onClick: () => navigate("/orientacion/insumos") });
    }
    if (informesVencidos.length > 0 && (role === "orientador" || role === "director" || role === "sostenedor")) {
      criticalActions.push({ label: `${informesVencidos.length} informe${informesVencidos.length !== 1 ? "s" : ""} para tribunal vence${informesVencidos.length !== 1 ? "n" : ""}`, icon: "ri-file-warning-line", count: informesVencidos.length, onClick: () => navigate("/orientacion/informes") });
    }
    if (roleCriticos.length > 0 && roleCriticos.filter((c) => c.estado === "abierto").length > 0) {
      const sinInterv = roleCriticos.filter((c) => c.estado === "abierto");
      criticalActions.push({ label: `${sinInterv.length} caso${sinInterv.length !== 1 ? "s" : ""} crítico${sinInterv.length !== 1 ? "s" : ""} sin intervención`, icon: "ri-alert-fill", count: sinInterv.length, onClick: () => navigate("/convivencia/casos") });
    }

    // Next best action cases
    const nbaCases = roleCases.map((c) => ({
      id: c.id,
      studentName: c.studentName,
      estado: c.estado,
      prioridad: c.prioridad,
      judicializado: c.judicializado,
      diasSinAccion: (() => {
        const ints = casoInterventions[c.id] || [];
        if (ints.length === 0) return daysBetween(c.fechaApertura, todayStr);
        return daysBetween(ints[ints.length - 1].fecha, todayStr);
      })(),
      tieneInsumosVencidos: insumosVencidos.some((t) => t.caseId === c.id),
      tieneInformeVencido: informesVencidos.some((cr) => cr.caseId === c.id),
    }));

    // Compliance stats
    const totalMissionCases = roleCases.length;
    const completedMissions = roleCases.filter((c) => c.estado === "cerrado").length;
    const complianceRate = totalMissionCases > 0 ? Math.round((1 - (roleCriticos.length + casosEstancados.filter((c) => roleCases.some((rc) => rc.id === c.id)).length) / (totalMissionCases * 2)) * 100) : 100;
    const cleanRate = casosCerradosMes.length > 0 ? Math.round((casosCerradosMes.length / (casosCerradosMes.length + roleCriticos.length)) * 100) : 100;

    return {
      role, roleCases, casosCriticos: roleCriticos, casosCerradosMes,
      informesVencidos, insumosVencidos, insumosPendientes,
      intervencionesSemana, casosEstancados,
      missions, criticalActions, nbaCases,
      complianceRate, cleanRate, casoInterventions,
    };
  }, [user, cases, courtRequestsState, teacherInputsState, interventionsState, todayStr]);

  if (!user || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="flex items-center justify-center w-8 h-8">
          <i className="ri-loader-4-line text-foreground-300 text-2xl animate-spin" />
        </span>
      </div>
    );
  }

  const roleConfig = ROLE_COLORS[data.role] || ROLE_COLORS.convivencia;
  const quickAccess = ROLE_QUICK_ACCESS[data.role] || DEFAULT_QUICK_ACCESS;
  const today = new Date().toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // Workflow stages
  const workflowStages: WorkflowStage[] = [
    { key: "detectar", label: "Detectar", icon: "ri-radar-line", status: data.roleCases.length > 0 ? "completed" : "current" },
    { key: "registrar", label: "Registrar", icon: "ri-file-add-line", status: data.roleCases.length > 0 ? "completed" : "pending" },
    { key: "solicitar", label: "Solicitar", icon: "ri-send-plane-line", status: data.insumosPendientes.length > 0 ? "current" : data.missions.every((m) => m.estado !== "esperando_insumos") ? "completed" : "pending" },
    { key: "revisar", label: "Revisar", icon: "ri-search-eye-line", status: data.roleCases.filter((c) => c.estado === "en_intervencion").length > 0 ? "current" : "pending" },
    { key: "informar", label: "Informar", icon: "ri-file-text-line", status: "pending" },
    { key: "validar", label: "Validar", icon: "ri-check-double-line", status: "pending" },
    { key: "enviar", label: "Enviar", icon: "ri-send-plane-fill", status: "pending" },
    { key: "cerrar", label: "Cerrar", icon: "ri-checkbox-circle-line", status: data.casosCerradosMes.length > 0 ? "current" : "pending", detail: `${data.casosCerradosMes.length} cerrados este mes` },
  ];

  return (
    <>
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-xs rounded-xl font-medium mb-4">
          {error}
        </div>
      )}
      {loading ? (
        <div className="space-y-6 animate-fade-in">
          <SkeletonHero />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonKPI key={i} />)}
          </div>
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
      <div className="space-y-6 animate-fade-in-up">
        {/* ═══════════════════════════════════════════ */}
        {/* CRITICAL ACTION BANNER */}
        {/* ═══════════════════════════════════════════ */}
        {data.criticalActions.length > 0 ? (
          <CriticalActionBanner
            greeting={greeting}
            userName={user.nombre.split(" ")[0]}
            actions={data.criticalActions}
            onResolveAll={() => data.criticalActions[0]?.onClick?.()}
          />
        ) : (
          <ScrollReveal className="scroll-reveal">
            <div className={`relative overflow-hidden rounded-3xl p-6 md:p-8 bg-gradient-to-br ${roleConfig.gradient} text-white shadow-soft-lg`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
              <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm text-white/60 mb-1">{today}</p>
                  <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Ezer Convivencia360</h1>
                  <p className="text-sm text-white/60 mt-1">{greeting}, {user.nombre.split(" ")[0]} — Sin alertas críticas. ¡Todo en orden!</p>
                </div>
                <span className="text-xs px-3 py-1.5 bg-white/15 backdrop-blur rounded-full font-medium border border-white/20 flex items-center gap-1.5 flex-shrink-0">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  Al día
                </span>
              </div>
            </div>
          </ScrollReveal>
        )}

        {/* ═══════════════════════════════════════════ */}
        {/* WORKFLOW PATH */}
        {/* ═══════════════════════════════════════════ */}
        <ScrollReveal className="scroll-reveal">
          <div className="card-modern p-5">
            <WorkflowPath stages={workflowStages} title="Ruta de gestión" />
          </div>
        </ScrollReveal>

        {/* ═══════════════════════════════════════════ */}
        {/* NEXT BEST ACTION + KPIs */}
        {/* ═══════════════════════════════════════════ */}
        <ScrollReveal className="scroll-reveal">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Next Best Action */}
            <div className="lg:col-span-2">
              <NextBestAction
                cases={data.nbaCases}
                role={data.role}
                onActionClick={(action) => {
                  if (action.caseId) navigate(`/casos/${action.caseId}`);
                  else if (action.type === "solicitar_insumo") navigate("/orientacion/insumos");
                  else if (action.type === "registrar_medida") navigate("/convivencia/medidas");
                  else if (action.type === "registrar_seguimiento") navigate("/psicologo/seguimiento");
                  else if (action.type === "descargar_reporte") navigate("/director/reportes/consolidado");
                  else if (action.type === "completar_formulario") navigate("/docente/solicitudes");
                }}
              />
            </div>

            {/* Compliance rings */}
            <div className="card-modern p-5">
              <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-4">Salud de gestión</p>
              <div className="flex items-center justify-around">
                <CompletionRing
                  progress={Math.max(0, Math.min(100, data.complianceRate))}
                  size="lg"
                  color={data.complianceRate >= 80 ? "emerald" : data.complianceRate >= 50 ? "amber" : "red"}
                  label="Cumplimiento"
                  sublabel="general"
                />
                <CompletionRing
                  progress={data.cleanRate}
                  size="lg"
                  color={data.cleanRate >= 80 ? "emerald" : data.cleanRate >= 50 ? "amber" : "red"}
                  label="Resolución"
                  sublabel="casos cerrados"
                />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <div className="text-center bg-background-50 rounded-xl p-2 scroll-reveal" style={{ transitionDelay: "100ms" }}>
                  <p className="text-lg font-bold text-foreground-900">{data.roleCases.length}</p>
                  <p className="text-[10px] text-foreground-400">Casos activos</p>
                </div>
                <div className="text-center bg-background-50 rounded-xl p-2 scroll-reveal" style={{ transitionDelay: "180ms" }}>
                  <p className="text-lg font-bold text-foreground-900">{data.intervencionesSemana.length}</p>
                  <p className="text-[10px] text-foreground-400">Intervenciones</p>
                </div>
                <div className={`text-center rounded-xl p-2 scroll-reveal ${data.casosCriticos.length > 0 ? "bg-red-50" : "bg-background-50"}`} style={{ transitionDelay: "260ms" }}>
                  <p className={`text-lg font-bold ${data.casosCriticos.length > 0 ? "text-red-600" : "text-foreground-900"}`}>{data.casosCriticos.length}</p>
                  <p className="text-[10px] text-foreground-400">Críticos</p>
                </div>
                <div className={`text-center rounded-xl p-2 scroll-reveal ${data.casosEstancados.length > 0 ? "bg-orange-50" : "bg-background-50"}`} style={{ transitionDelay: "340ms" }}>
                  <p className={`text-lg font-bold ${data.casosEstancados.length > 0 ? "text-orange-600" : "text-foreground-900"}`}>{data.casosEstancados.length}</p>
                  <p className="text-[10px] text-foreground-400">Estancados</p>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* ═══════════════════════════════════════════ */}
        {/* MISSION CARDS */}
        {/* ═══════════════════════════════════════════ */}
        <ScrollRevealContainer
          header={(
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider">Misiones activas</p>
              <button onClick={() => navigate("/convivencia/casos")} className="text-xs text-primary-600 font-semibold hover:text-primary-700 cursor-pointer whitespace-nowrap">
                Ver todas →
              </button>
            </div>
          )}
          staggerDelay={60}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {data.missions.map((caso, idx) => {
              const mission = getMissionStatus(caso, todayStr, data.casoInterventions, data.insumosVencidos);
              const progress = caso.estado === "cerrado" ? 100
                : caso.estado === "enviado_tribunal" ? 90
                : caso.estado === "informe_generado" ? 75
                : caso.estado === "en_seguimiento" ? 60
                : caso.estado === "en_intervencion" ? 45
                : caso.estado === "en_evaluacion" ? 25
                : caso.estado === "abierto" ? 10
                : 5;
              const ints = data.casoInterventions[caso.id] || [];
              const daysSince = ints.length > 0
                ? daysBetween(ints[ints.length - 1].fecha, todayStr)
                : daysBetween(caso.fechaApertura, todayStr);

              return (
                <div key={caso.id} className="scroll-reveal" style={{ transitionDelay: `${idx * 60}ms` }}>
                  <MissionCard
                    title={caso.studentName}
                    subtitle={caso.titulo}
                    status={mission.status}
                    icon={caso.judicializado ? "ri-scales-3-line" : caso.categoria === "convivencia" ? "ri-shield-user-line" : caso.categoria === "orientacion" ? "ri-compass-3-line" : "ri-psychotherapy-line"}
                    color={mission.status === "urgent" ? "red" : mission.status === "active" ? "primary" : mission.status === "pending" ? "amber" : "emerald"}
                    progress={progress}
                    deadline={caso.fechaLimite}
                    daysRemaining={daysBetween(todayStr, caso.fechaLimite)}
                    badge={mission.badge}
                    onClick={() => navigate(`/casos/${caso.id}`)}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${mission.status === "urgent" ? "bg-red-500 animate-pulse" : "bg-primary-400"}`} />
                      <span className="text-xs text-foreground-500">{daysSince}d desde última acción</span>
                    </div>
                  </MissionCard>
                </div>
              );
            })}
            {data.missions.length === 0 && (
              <div className="col-span-full text-center py-8">
                <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                  <span className="flex items-center justify-center w-8 h-8">
                    <i className="ri-check-double-line text-emerald-400 text-2xl" />
                  </span>
                </div>
                <p className="text-sm font-bold text-foreground-800">Sin misiones activas</p>
                <p className="text-xs text-foreground-500 mt-1">Todos los casos están al día.</p>
              </div>
            )}
          </div>
        </ScrollRevealContainer>

        {/* ═══════════════════════════════════════════ */}
        {/* QUICK ACCESS PANEL */}
        {/* ═══════════════════════════════════════════ */}
        <ScrollRevealContainer
          header={<p className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3">Accesos rápidos</p>}
          staggerDelay={40}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {quickAccess.map((item, idx) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`${item.bg} border border-transparent rounded-2xl p-4 flex flex-col items-center gap-2.5 hover:scale-[1.03] hover:shadow-soft-md transition-all duration-300 ease-out-expo cursor-pointer scroll-reveal group`}
                style={{ transitionDelay: `${idx * 40}ms` }}
              >
                <span className={`flex items-center justify-center w-11 h-11 rounded-xl ${item.color} bg-white shadow-soft flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${item.icon} text-xl`} />
                </span>
                <span className="text-xs font-bold text-foreground-800 text-center whitespace-nowrap leading-tight">{item.label}</span>
              </button>
            ))}
          </div>
        </ScrollRevealContainer>

        {/* ═══════════════════════════════════════════ */}
        {/* WEEKLY TRACKER for Director/Sostenedor */}
        {/* ═══════════════════════════════════════════ */}
        {(data.role === "director" || data.role === "sostenedor") && (
          <ScrollRevealContainer
            header={(
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider">Ruta de avance semanal</p>
                <span className="text-[10px] text-foreground-400">Semana 24 · Junio 2026</span>
              </div>
            )}
            staggerDelay={80}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Convivencia Escolar", icon: "ri-shield-user-line", color: "primary", path: "/convivencia", casos: data.roleCases.filter((c) => ["convivencia", "conducta"].includes(c.categoria)).length, done: data.casosCerradosMes.filter((c) => ["convivencia", "conducta"].includes(c.categoria)).length },
                { label: "Orientación", icon: "ri-compass-3-line", color: "purple", path: "/orientacion", casos: data.roleCases.filter((c) => ["orientacion", "asistencia"].includes(c.categoria)).length, done: data.casosCerradosMes.filter((c) => ["orientacion", "asistencia"].includes(c.categoria)).length },
                { label: "Psicología", icon: "ri-psychotherapy-line", color: "teal", path: "/psicologo", casos: data.roleCases.filter((c) => ["psicologia", "vulneracion"].includes(c.categoria)).length, done: data.casosCerradosMes.filter((c) => ["psicologia", "vulneracion"].includes(c.categoria)).length },
                { label: "Judicial", icon: "ri-scales-3-line", color: "amber", path: "/judicial", casos: data.roleCases.filter((c) => c.judicializado).length, done: data.informesVencidos.length > 0 ? data.informesVencidos.length * -1 : 0 },
              ].map((area, idx) => (
                <button
                  key={area.label}
                  onClick={() => navigate(area.path)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50 hover:bg-background-100 transition-colors cursor-pointer group text-center scroll-reveal"
                  style={{ transitionDelay: `${idx * 80}ms` }}
                >
                  <span className={`flex items-center justify-center w-12 h-12 rounded-2xl bg-${area.color}-50`}>
                    <i className={`${area.icon} text-${area.color}-500 text-xl`} />
                  </span>
                  <p className="text-xs font-bold text-foreground-800">{area.label}</p>
                  <div className="flex items-center gap-3 text-[10px] text-foreground-400">
                    <span>{area.casos} activos</span>
                    <span className="text-emerald-500 font-medium">{area.done} resueltos</span>
                  </div>
                </button>
              ))}
            </div>
          </ScrollRevealContainer>
        )}
      </div>
      )}
    </>
  );
}